import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { getFrameStorage } from "../storage";
import { selectChallengeHeaders, selectEffectiveLocale } from "./selectors";
import log from "../log";
import { ApiError, getDeviceInformation } from "./sharedActions";
import { UserData } from "../chat/types";

// Define the types for the request and response
export interface GetCommandsRequest {
  ping: {
    user_data: any;
    locales: string[];
    locale: string;
    timezone: string;
    timezone_offset: number;
  };
  url: string;
  active_conversation_id: string | null;
  goal: "interaction" | "resolution";
  engagement:
    | "product discovery"
    | "cross selling"
    | "upselling"
    | "intent boosting"
    | "help"
    | "";
}

export interface GetCommandsResponse {
  frameId: string;
  data: {
    user_data: UserData;
    sdk_actions: Array<[string, string] | any[]>;
  };
  headers: {
    device_id: string;
    user_id: string;
    session_id: string;
  };
}

export const getEngagementActions = createAsyncThunk<
  GetCommandsResponse,
  {
    goal: "interaction" | "resolution";
    engagement:
      | "product discovery"
      | "cross selling"
      | "upselling"
      | "intent boosting"
      | "help"
      | "";
  },
  {
    state: RootState;
    rejectValue: ReturnType<ApiError["toJSON"]>;
  }
>("shared/getEngagementActions", async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const { apiBase, frameId } = state.config.data;
  const { deviceId, userId, sessionId } = getFrameStorage(frameId);
  const { locales, timezone, timezone_offset } = getDeviceInformation();
  const { user_data } = state.user.data;

  const reqHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...selectChallengeHeaders(frameId)(state),
    ...(deviceId && { "X-Device-Id": deviceId }),
    ...(userId && { "X-User-Id": userId }),
    ...(sessionId && { "X-Session-Id": sessionId }),
  };

  const reqBody: GetCommandsRequest = {
    ping: {
      user_data,
      locales,
      locale:
        state.config.data.locale ?? selectEffectiveLocale(state) ?? "en-US",
      timezone,
      timezone_offset,
    },
    url: window.location.href, // Current URL from browser
    active_conversation_id: state.chat.conversationId || null,

    goal: payload.goal,
    engagement: payload.engagement,
  };

  try {
    log.debug("getCommands request", reqBody);
    const response = await fetch(`${apiBase}/v1/frames/${frameId}/engage`, {
      method: "POST",
      headers: reqHeaders,
      body: JSON.stringify(reqBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const message =
        response.status == 429
          ? "Too many requests. Please try again later."
          : errorData?.message || `API Error: ${response.status}`;

      throw new ApiError(
        response.status,
        Object.fromEntries(response.headers.entries()),
        errorData,
        message
      );
    }

    const responseData = await response.json();
    return {
      data: responseData,
      headers: {
        device_id: deviceId,
        user_id: response.headers.get("X-User-Id") || "",
        session_id: response.headers.get("X-Session-Id") || "",
      },
      frameId,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      console.error("[botbrains] API ERROR ", error.status, error.toJSON());
      return rejectWithValue(error.toJSON());
    }

    // Create a normalized API error for unexpected errors
    const unexpectedError = new ApiError(
      500,
      {},
      { message: error instanceof Error ? error.message : "Unknown error" },
      "Unexpected error occurred"
    );
    return rejectWithValue(unexpectedError.toJSON());
  }
});

